import { useRef, useState, type ChangeEvent, type Dispatch, type SetStateAction } from "react";
import type { Pedido } from "../types/pedido";
import { supabase } from "../lib/supabase";
import { fetchDatosReporte } from "../lib/reportes/datos";
import { generarReporteCliente } from "../lib/reportes/reporte-cliente";
import { generarBitacora } from "../lib/reportes/bitacora";
import { subirReporteCliente, subirBitacora } from "../lib/storage";

interface FinalizarFields {
  contacto: string;
  nombreMuestra: string;
  atencion: string;
  autorizo: string;
  superviso: string;
  numeroAcreditacion: string;
  imagen: File | null;
}

interface UseFinalizarPedidoReturn {
  showFinalizarModal: boolean;
  setShowFinalizarModal: Dispatch<SetStateAction<boolean>>;
  finalizarFields: FinalizarFields;
  setFinalizarFields: Dispatch<SetStateAction<FinalizarFields>>;
  finalizarLoading: boolean;
  isSaving: boolean;
  generandoPDFs: boolean;
  imagenError: string | null;
  handleImageChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleSave: () => Promise<void>;
}

interface UseFinalizarPedidoOptions {
  onSuccess?: () => void;
  onPDFsGenerados?: (clienteUrl: string, bitacoraUrl: string) => void;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => resolve(e.target?.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function useFinalizarPedido(pedido: Pedido, options: UseFinalizarPedidoOptions = {}): UseFinalizarPedidoReturn {
  const { onSuccess, onPDFsGenerados } = options;
  const [showFinalizarModal, setShowFinalizarModal] = useState(false);
  const [finalizarFields, setFinalizarFields] = useState<FinalizarFields>({
    contacto: '',
    nombreMuestra: '',
    atencion: '',
    autorizo: '',
    superviso: '',
    numeroAcreditacion: '',
    imagen: null,
  });
  const [finalizarLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [generandoPDFs, setGenerandoPDFs] = useState(false);
  const [imagenError, setImagenError] = useState<string | null>(null);
  const guardandoRef = useRef(false);

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      setFinalizarFields(f => ({ ...f, imagen: null }));
      setImagenError(null);
      return;
    }
    const allowedTypes = ['image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setFinalizarFields(f => ({ ...f, imagen: null }));
      setImagenError('Solo se permiten imágenes JPG o PNG');
      return;
    }
    setImagenError(null);
    setFinalizarFields(f => ({ ...f, imagen: file }));
  };

  const handleSave = async () => {
    if (isSaving || guardandoRef.current) return;
    guardandoRef.current = true;
    setIsSaving(true);

    try {
      // Convertir imagen a base64 para almacenar en DB
      const testigoBase64 = finalizarFields.imagen
        ? await fileToBase64(finalizarFields.imagen)
        : null;

      // Actualizar pedido en Supabase con todos los campos de cierre
      const { data, error } = await supabase
        .from('pedidos')
        .update({
          cerrar_pedido: 'true',
          contacto: finalizarFields.contacto,
          muestra: finalizarFields.nombreMuestra,
          con_atencion: finalizarFields.atencion,
          autorizo: finalizarFields.autorizo,
          superviso: finalizarFields.superviso,
          no_acreditacion: finalizarFields.numeroAcreditacion,
          ...(testigoBase64 ? { testigo: testigoBase64 } : {}),
        })
        .eq('id', pedido.id)
        .select();

      if (error) throw error;
      if (!data || data.length === 0) throw new Error('Sin permiso para cerrar este pedido (RLS bloqueó el update).');

      setShowFinalizarModal(false);
      onSuccess?.();

      // Generar PDFs en background — deshabilita botones de descarga hasta terminar
      const pedidoId = pedido.id;
      setGenerandoPDFs(true);
      (async () => {
        try {
          const datos = await fetchDatosReporte(pedidoId);
          const [htmlCliente, htmlBitacora] = [
            generarReporteCliente(datos),
            generarBitacora(datos),
          ];
          const [clienteUrl, bitacoraUrl] = await Promise.all([
            subirReporteCliente(pedidoId, pedido.numeroPedido, htmlCliente),
            subirBitacora(pedidoId, pedido.numeroPedido, htmlBitacora),
          ]);
          onPDFsGenerados?.(clienteUrl, bitacoraUrl);
        } catch (err) {
          console.error('Error generando PDFs al cerrar pedido:', err);
        } finally {
          setGenerandoPDFs(false);
        }
      })();
    } catch (error) {
      console.error('Error al cerrar pedido:', error);
      alert('Hubo un error al cerrar el pedido. Por favor, inténtelo de nuevo.');
    } finally {
      setIsSaving(false);
      guardandoRef.current = false;
    }
  };

  return {
    showFinalizarModal,
    setShowFinalizarModal,
    finalizarFields,
    setFinalizarFields,
    finalizarLoading,
    isSaving,
    generandoPDFs,
    imagenError,
    handleImageChange,
    handleSave,
  };
}
